import createGenericItem from './createGenericItem';
import {
    populateFeedbackStateCache,
    resetFeedbackState,
} from 'owa-readwrite-recipient-well/lib/utils/populateFeedbackState';
import updateQueryString from '../actions/updateQueryString';
import FindResultRecipient from '../components/FindResultRecipient';
import getSuggestionProps from '../utils/getSuggestionProps';
import { ITheme } from '@uifabric/styling';
import pauseSession from 'owa-controls-findpeople-feedback-manager/lib/actions/pauseSession';
import resumeSession from 'owa-controls-findpeople-feedback-manager/lib/actions/resumeSession';
import { now } from 'owa-datetime';
import updateCurrentRenderedQueryString from 'owa-readwrite-recipient-well/lib/actions/updateCurrentRenderedQueryString';
import findReadWriteRecipient from 'owa-readwrite-recipient-well/lib/actions/findReadWriteRecipient';
import isValidAddressWithOptionalDisplayName from 'owa-recipient-email-address/lib/utils/isValidAddressWithOptionalDisplayName';
import getAndPopulateSuggestedContacts from 'owa-recipient-cache/lib/actions/getAndPopulateSuggestedContacts';
import FindControlViewState from 'owa-recipient-types/lib/types/FindControlViewState';
import FindRecipientPersonaType from 'owa-recipient-types/lib/types/FindRecipientPersonaType';
import ReadWriteRecipientViewState from 'owa-recipient-types/lib/types/ReadWriteRecipientViewState';
import addLocalCacheTransactionComplete from 'owa-recipient-common/lib/utils/addLocalCacheTransactionComplete';
import getPersonasFromReadWriteRecipientList from 'owa-recipient-common/lib/utils/getPersonasFromReadWriteRecipientList';
import maskRecipientFromFindControl from 'owa-readwrite-recipient-well/lib/actions/maskRecipientFromFindControl';
import { getRecipientAriaLabel } from './getRecipientAriaLabel';
import { logUsage } from 'owa-analytics';

import {
    FloatingPeoplePicker,
    IPeopleFloatingPickerProps,
    SuggestionsStore,
} from 'office-ui-fabric-react/lib/FloatingPicker';

export default function getFloatingPickerProps(
    getViewState: () => FindControlViewState,
    getFindControl: () => FloatingPeoplePicker,
    controlledComponent: boolean,
    theme?: ITheme,
    onPickerHidden?: () => void
): IPeopleFloatingPickerProps {
    return {
        suggestionsStore: new SuggestionsStore<FindRecipientPersonaType>({
            getAriaLabel: getRecipientAriaLabel,
        }),
        onValidateInput: isValidAddressWithOptionalDisplayName,
        onRenderSuggestionsItem: onRenderSuggestion(
            getViewState,
            getFindControl,
            controlledComponent,
            theme
        ),
        createGenericItem: createGenericItem,
        onResolveSuggestions: controlledComponent
            ? getQuerySuggestions(getViewState())
            : getQuerySuggestionsSynchronous(getViewState()),
        onZeroQuerySuggestion: controlledComponent
            ? getZeroQuerySuggestions(getViewState())
            : getZeroQuerySuggestionsSynchronous(getViewState()),
        calloutWidth: 300,
        resolveDelay: 150,
        onRemoveSuggestion: onMaskRecipient(getFindControl, getViewState, controlledComponent),
        getTextFromItem: (item: FindRecipientPersonaType) => {
            return item.EmailAddress.Name;
        },
        pickerSuggestionsProps: getSuggestionProps(
            getViewState,
            getFindControl,
            controlledComponent,
            theme
        ),
        onSuggestionsShown: onSuggestionsShown(getViewState()),
        onSuggestionsHidden: onSuggestionsHidden(getViewState(), onPickerHidden),
    };
}

function onMaskRecipient(
    getFindControl: () => FloatingPeoplePicker,
    getViewState: () => FindControlViewState,
    controlledComponent: boolean
): (recipientToMask: FindRecipientPersonaType) => void {
    return function(recipientToMask: FindRecipientPersonaType): void {
        logUsage('RWFindPeopleMasking');
        const viewState = getViewState();
        maskRecipientFromFindControl(viewState, recipientToMask);

        if (!controlledComponent) {
            getFindControl().updateSuggestions(viewState.findResultSet.slice(), true /*updateUI*/);
        }
    };
}

function onRenderSuggestion(
    getViewState: () => FindControlViewState,
    getFindControl: () => FloatingPeoplePicker,
    controlledComponent: boolean,
    theme: ITheme
): (props: FindRecipientPersonaType, itemProps: any) => JSX.Element {
    return function(props: FindRecipientPersonaType, itemProps: any): JSX.Element {
        return FindResultRecipient({
            itemProps: itemProps,
            persona: props,
            theme: theme,
        });
    };
}

/* This won't be needed after vso: 35556 */ function getQuerySuggestionsSynchronous(
    viewState: FindControlViewState
): (
    filterText: string,
    recipients: ReadWriteRecipientViewState[]
) => Promise<FindRecipientPersonaType[]> {
    return async function(filterText: string, recipients: ReadWriteRecipientViewState[]) {
        if (viewState.queryString === filterText) {
            await findReadWriteRecipient(viewState, filterText, false /*searchDirectory*/);
            return viewState.findResultSet.slice();
        }

        return null;
    };
}

function getQuerySuggestions(
    viewState: FindControlViewState
): (filterText: string, recipients: ReadWriteRecipientViewState[]) => FindRecipientPersonaType[] {
    return function(filterText: string, recipients: ReadWriteRecipientViewState[]) {
        if (viewState.queryString === filterText) {
            findReadWriteRecipient(viewState, filterText, false /*searchDirectory*/);
        }
        return null;
    };
}

/* This won't be needed after vso: 35556 */ function getZeroQuerySuggestionsSynchronous(
    viewState: FindControlViewState
): (recipients: ReadWriteRecipientViewState[]) => Promise<FindRecipientPersonaType[]> {
    return async function(recipients: ReadWriteRecipientViewState[]) {
        updateQueryString(viewState, '');
        updateCurrentRenderedQueryString(viewState, '');
        const transactionStartTime = now();
        resetFeedbackState(viewState);
        getAndPopulateSuggestedContacts(
            viewState.findResultSet,
            getPersonasFromReadWriteRecipientList(recipients)
        );
        populateFeedbackStateCache(viewState, transactionStartTime, viewState.findResultSet);
        addLocalCacheTransactionComplete(viewState);
        return viewState.findResultSet.slice();
    };
}

function getZeroQuerySuggestions(
    viewState: FindControlViewState
): (recipients: ReadWriteRecipientViewState[]) => FindRecipientPersonaType[] {
    return function(recipients: ReadWriteRecipientViewState[]) {
        updateQueryString(viewState, '');
        updateCurrentRenderedQueryString(viewState, '');
        const transactionStartTime = now();
        resetFeedbackState(viewState);
        getAndPopulateSuggestedContacts(
            viewState.findResultSet,
            getPersonasFromReadWriteRecipientList(recipients)
        );
        populateFeedbackStateCache(viewState, transactionStartTime, viewState.findResultSet);
        addLocalCacheTransactionComplete(viewState);
        return null;
    };
}

function onSuggestionsShown(viewState: FindControlViewState): () => void {
    return function() {
        resumeSession(viewState.findPeopleFeedbackManager);
    };
}

function onSuggestionsHidden(
    viewState: FindControlViewState,
    onPickerHidden?: () => void
): () => void {
    return function() {
        onPickerHidden && onPickerHidden();
        pauseSession(viewState.findPeopleFeedbackManager);
    };
}
